import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./invisalign-for-teens.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { OptimizedImage } from "~/components/OptimizedImage";

type Localization = {
  title: string;
  heading: string;
  // Intro Section
  introTitle: string;
  introP1: string;
  introP2: string;
  introP3: string;
  // Almost Invisible Section
  invisibleTitle: string;
  invisibleP1: string;
  invisibleP2: string;
  invisibleP3: string;
  invisibleP4: string;
  // Advantages Section
  advantagesTitle: string;
  advantagesList: string[];
  // Busy Schedules Section
  busyTitle: string;
  busyP1: string;
  busyP2: string;
  // Active Teens Section
  activeTitle: string;
  activeP1: string;
  activeP2: string;
  activeP3: string;
  // Cost Section
  costTitle: string;
  costP1: string;
  costP2: string;
  // Talk to Us Section
  talkTitle: string;
  talkP1: string;
  talkP2: string;
  talkP3: string;
  talkP4: string;
};

const englishLocalization: Localization = {
  title: "Invisalign for Teens",
  heading: "Your Teen Will Love Invisalign®",

  introTitle: "Your Teen Will Love Invisalign®",
  introP1:
    "Now, there is a way to get straight teeth as a teen or tween without having braces on the teeth. Invisalign for teens was developed specifically for teenagers. It involves a series of clear aligners (trays) that gradually move the teeth to their final position.",
  introP2:
    "Straightening your teeth with Invisalign can be a great option if your teen is concerned about their appearance and don't want to look different from their friends.",
  introP3:
    "Invisalign created a system that meets the special needs of teens. Invisalign for Teens can be a great clear option for teens shying away from traditional metal braces.",

  invisibleTitle: "Almost Invisible",
  invisibleP1:
    "The Invisalign for Teens aligners are made of clear plastic and almost invisible. Your teen's friends probably won't even know they're wearing them. Instead of hiding behind a mouth full of metal, they'll enjoy the confidence of showing that smile every day.",
  invisibleP2:
    "They probably have friends with braces who aren't allowed to chew gum or eat certain foods like popcorn and whole apples. With Invisalign Teen, the aligners are completely removable, allowing your teen to eat all of your favorite foods whenever you want.",
  invisibleP3:
    "This also means your teen can easily keep brushing and flossing as you normally would for proper oral hygiene. Try that with braces.",
  invisibleP4:
    "Whether your child plays sports or a musical instrument, a mouth full of metal can hold them back from enjoying the things they want to. It's hard to perform at your best knowing you could get hit in the mouth with a ball, or cut your lip trying to hit that note on the trumpet. Invisalign for Teens helps your child focus on doing what they do best—being a teen.",

  advantagesTitle: "Advantages of Invisalign Treatment",
  advantagesList: [
    "More cosmetic: Invisalign wearers can continue their normal lifestyle without feeling the need to hide their smile in photos. Quinces pictures coming up? Graduation pictures scheduled this summer? No problem! The Invisalign aligners can be easily removed during photo shoots, but are generally not seen in photos anyway. Snap away.",
    "More hygienic: Invisalign aligners are removable for easy access during brushing and flossing. No special tools are required to keep your whole mouth clean and your breath smelling fresh. To clean the aligners, you can use special Invisalign cleaning tabs or simply brush and rinse them under warm water.",
    "Eat whatever you want: Since Invisalign is removable, you also don't need to worry about getting food stuck in your teeth or foods breaking the aligners. Popcorn and gum are no longer a concern.",
    "Easier for athletes: Less risk of injury from wires or brackets",
    "Easier for musicians: The trays can be easily removed while singing in a concert or playing a wind instrument",
    "More comfortable: The Invisalign aligners are custom-fit to each patient's teeth. There are no metal braces or wires rubbing against the inside of lips and cheeks. There is also a shorter adjustment period when treatment is first started.",
  ],

  busyTitle: "Perfect for Busy Schedules",
  busyP1:
    'Invisalign for Teens treatment is ideal for busy parents because there are never any emergency appointments from broken wires or brackets. Invisalign often requires fewer appointments (than traditional metal braces) because you get your Invisalign aligners in advance, and each visit requires less "chair time."',
  busyP2:
    "The typical Invisalign for Teens patient is seen once every 8 weeks for a total average treatment time of 9 to 15 months.",

  activeTitle: "Great for Active Teens",
  activeP1:
    "Invisalign for Teens effectively straightens teeth the same way metal braces do. Invisalign for Teens is an ideal choice for teens who play sports, play certain types of instruments, love crunchy foods, or are worried about how metal braces will look at homecoming or in the yearbook.",
  activeP2:
    "As a teenager, you should be able to participate in the activities you're interested in while still having the confidence to attend social events and make lasting memories. You should also be able to get efficient orthodontic treatment for crowded teeth, uneven spacing, and bite issues.",
  activeP3:
    "With the clear, comfortable technology of Invisalign, teens can keep being themselves and love their smiles even more.",

  costTitle: "Doesn't Cost More than Traditional Braces",
  costP1:
    "Your needs are unique, and many factors determine the cost of Invisalign treatment. In most cases, Invisalign is a similar price as traditional braces and covered by most dental insurance plans.",
  costP2:
    "Financing options are also available to make Invisalign more affordable for your family.",

  talkTitle: "Talk to Us About Invisalign",
  talkP1:
    "It's important to wear your aligners for at least 22 hours each day for them to do their job and straighten your teeth. But, with Invisalign, you can remove your aligners for special occasions.",
  talkP2:
    "Like with traditional braces, you will generally need to wear a retainer after Invisalign treatment to maintain your new alignment—and your confident new smile.",
  talkP3:
    "Invisalign may not be recommended for some complex alignment or bite issues, or for those who have bridgework; the doctor can advise you about what options are best for you.",
  talkP4:
    "Schedule your free consultation at our Miami, FL office to learn if Invisalign is right for you. Follow our social to see our wonderful Invisalign patients.",
};

const spanishLocalization: Localization = {
  title: "Invisalign para Adolescentes",
  heading: "A tu Adolescente le Encantará Invisalign®",

  introTitle: "A tu Adolescente le Encantará Invisalign®",
  introP1:
    "Ahora, hay una manera de obtener dientes rectos como adolescente o preadolescente sin tener brackets en los dientes. Invisalign para adolescentes fue desarrollado específicamente para adolescentes. Consiste en una serie de alineadores transparentes (bandejas) que mueven gradualmente los dientes a su posición final.",
  introP2:
    "Enderezar tus dientes con Invisalign puede ser una gran opción si tu adolescente está preocupado por su apariencia y no quiere verse diferente a sus amigos.",
  introP3:
    "Invisalign creó un sistema que satisface las necesidades especiales de los adolescentes. Invisalign para Adolescentes puede ser una gran opción transparente para adolescentes que evitan los brackets metálicos tradicionales.",

  invisibleTitle: "Casi Invisible",
  invisibleP1:
    "Los alineadores de Invisalign para Adolescentes están hechos de plástico transparente y son casi invisibles. Los amigos de tu adolescente probablemente ni siquiera sabrán que los está usando. En lugar de esconderse detrás de una boca llena de metal, disfrutarán de la confianza de mostrar esa sonrisa todos los días.",
  invisibleP2:
    "Probablemente tengan amigos con brackets a los que no se les permite masticar chicle o comer ciertos alimentos como palomitas de maíz y manzanas enteras. Con Invisalign Teen, los alineadores son completamente removibles, lo que permite a tu adolescente comer todos sus alimentos favoritos cuando quiera.",
  invisibleP3:
    "Esto también significa que tu adolescente puede seguir cepillándose los dientes y usando hilo dental como lo haría normalmente para una higiene bucal adecuada. Intenta eso con brackets.",
  invisibleP4:
    "Ya sea que tu hijo practique deportes o toque un instrumento musical, una boca llena de metal puede impedirle disfrutar de las cosas que quiere. Es difícil rendir al máximo sabiendo que podrías recibir un golpe en la boca con una pelota, o cortarte el labio tratando de alcanzar esa nota en la trompeta. Invisalign para Adolescentes ayuda a tu hijo a concentrarse en hacer lo que mejor sabe hacer: ser un adolescente.",

  advantagesTitle: "Ventajas del Tratamiento Invisalign",
  advantagesList: [
    "Más estético: Los usuarios de Invisalign pueden continuar con su estilo de vida normal sin sentir la necesidad de ocultar su sonrisa en las fotos. ¿Fotos de quinceañera próximamente? ¿Fotos de graduación programadas para este verano? ¡No hay problema! Los alineadores Invisalign se pueden quitar fácilmente durante las sesiones de fotos, pero generalmente no se ven en las fotos de todos modos. Toma todas las fotos que quieras.",
    "Más higiénico: Los alineadores Invisalign son removibles para facilitar el acceso durante el cepillado y el uso del hilo dental. No se requieren herramientas especiales para mantener toda la boca limpia y el aliento fresco. Para limpiar los alineadores, puedes usar tabletas especiales de limpieza Invisalign o simplemente cepillarlos y enjuagarlos con agua tibia.",
    "Come lo que quieras: Como Invisalign es removible, tampoco tienes que preocuparte por que la comida se atasque en tus dientes o por que los alimentos rompan los alineadores. Las palomitas de maíz y el chicle ya no son una preocupación.",
    "Más fácil para atletas: Menos riesgo de lesiones por alambres o brackets",
    "Más fácil para músicos: Las bandejas se pueden quitar fácilmente mientras cantas en un concierto o tocas un instrumento de viento",
    "Más cómodo: Los alineadores Invisalign están hechos a medida para los dientes de cada paciente. No hay brackets ni alambres metálicos que rocen contra el interior de los labios y las mejillas. También hay un período de ajuste más corto cuando se inicia el tratamiento.",
  ],

  busyTitle: "Perfecto para Horarios Ocupados",
  busyP1:
    'El tratamiento Invisalign para Adolescentes es ideal para padres ocupados porque nunca hay citas de emergencia por alambres o brackets rotos. Invisalign a menudo requiere menos citas (que los brackets metálicos tradicionales) porque recibes tus alineadores Invisalign por adelantado, y cada visita requiere menos "tiempo en el sillón".',
  busyP2:
    "El paciente típico de Invisalign para Adolescentes es visto una vez cada 8 semanas por un tiempo total promedio de tratamiento de 9 a 15 meses.",

  activeTitle: "Excelente para Adolescentes Activos",
  activeP1:
    "Invisalign para Adolescentes endereza los dientes de manera efectiva de la misma manera que lo hacen los brackets metálicos. Invisalign para Adolescentes es una opción ideal para adolescentes que practican deportes, tocan ciertos tipos de instrumentos, aman los alimentos crujientes o están preocupados por cómo se verán los brackets metálicos en el baile de bienvenida o en el anuario.",
  activeP2:
    "Como adolescente, deberías poder participar en las actividades que te interesan mientras tienes la confianza para asistir a eventos sociales y crear recuerdos duraderos. También deberías poder obtener un tratamiento ortodóncico eficiente para dientes apiñados, espaciado desigual y problemas de mordida.",
  activeP3:
    "Con la tecnología clara y cómoda de Invisalign, los adolescentes pueden seguir siendo ellos mismos y amar aún más sus sonrisas.",

  costTitle: "No Cuesta Más que los Brackets Tradicionales",
  costP1:
    "Tus necesidades son únicas, y muchos factores determinan el costo del tratamiento Invisalign. En la mayoría de los casos, Invisalign tiene un precio similar al de los brackets tradicionales y está cubierto por la mayoría de los planes de seguro dental.",
  costP2:
    "También hay opciones de financiamiento disponibles para hacer que Invisalign sea más asequible para tu familia.",

  talkTitle: "Habla con Nosotros Sobre Invisalign",
  talkP1:
    "Es importante usar tus alineadores durante al menos 22 horas cada día para que hagan su trabajo y enderecen tus dientes. Pero, con Invisalign, puedes quitarte los alineadores para ocasiones especiales.",
  talkP2:
    "Al igual que con los brackets tradicionales, generalmente necesitarás usar un retenedor después del tratamiento Invisalign para mantener tu nueva alineación y tu nueva sonrisa confiada.",
  talkP3:
    "Es posible que Invisalign no se recomiende para algunos problemas complejos de alineación o mordida, o para aquellos que tienen puentes dentales; el doctor puede aconsejarte sobre qué opciones son mejores para ti.",
  talkP4:
    "Programa tu consulta gratuita en nuestra oficina de Miami, FL para saber si Invisalign es adecuado para ti. Sigue nuestras redes sociales para ver a nuestros maravillosos pacientes de Invisalign.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function InvisalignForTeensPage() {
  const getLocalization = useLocalization(localized);

  return (
    <>
      <HeroStyle2
        heroImageUrl="/invisalign-for-teens.jpg"
        title={getLocalization("title")}
        backgroundPosition="top"
      />

      <Flex
        className={`section ${styles.mainContentSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          direction="column"
          className={styles.contentSection}
          gap="8"
          p="5"
        >
          {/* Intro Section */}
          <Flex direction="column" gap="6">
            <Heading as="h2" className={styles.heading}>
              {getLocalization("introTitle")}
            </Heading>
            <Text size="5">{getLocalization("introP1")}</Text>
            <Text size="5">{getLocalization("introP2")}</Text>
            <Text size="5">{getLocalization("introP3")}</Text>
          </Flex>

          {/* Almost Invisible Section */}
          <Flex direction="column" gap="6">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("invisibleTitle")}
            </Heading>
            <Text size="5">{getLocalization("invisibleP1")}</Text>
            <Text size="5">{getLocalization("invisibleP2")}</Text>
            <Text size="5">{getLocalization("invisibleP3")}</Text>
            <Text size="5">{getLocalization("invisibleP4")}</Text>
          </Flex>

          <Box width="100%">
            <OptimizedImage
              src="/why-choose-us-2.jpg"
              alt="Invisalign for Teens Miami"
              width="100%"
              height="auto"
            />
          </Box>

          {/* Advantages Section */}
          <Flex direction="column" gap="6">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("advantagesTitle")}
            </Heading>
            <Flex asChild direction="column" gap="2">
              <ul>
                {getLocalization("advantagesList").map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Flex>
          </Flex>

          {/* Busy Schedules Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("busyTitle")}
            </Heading>
            <Text size="5">{getLocalization("busyP1")}</Text>
            <Text size="5">{getLocalization("busyP2")}</Text>
          </Flex>

          {/* Active Teens Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("activeTitle")}
            </Heading>
            <Text size="5">{getLocalization("activeP1")}</Text>
            <Text size="5">{getLocalization("activeP2")}</Text>
            <Text size="5">{getLocalization("activeP3")}</Text>
          </Flex>

          {/* Cost Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("costTitle")}
            </Heading>
            <Text size="5">{getLocalization("costP1")}</Text>
            <Text size="5">{getLocalization("costP2")}</Text>
          </Flex>

          {/* Talk to Us Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("talkTitle")}
            </Heading>
            <Text size="5">{getLocalization("talkP1")}</Text>
            <Text size="5">{getLocalization("talkP2")}</Text>
            <Text size="5">{getLocalization("talkP3")}</Text>
            <Text size="5">{getLocalization("talkP4")}</Text>
          </Flex>
        </Flex>
      </Flex>

      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
